/**
 * Landing Page Navigation Component
 * Sticky navigation bar for the landing page
 */

import React, { useState, useEffect } from 'react';
import { Button } from './Button';
import { NavThemePicker } from './NavThemePicker';
import { Menu, X } from 'lucide-react';
import { isAppDomain } from '../../utils/domain';

export const LandingNav: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    } else {
      // If section doesn't exist on current page, navigate to landing page with anchor.
      // Only the app hosts need to jump to the marketing site for those sections; on
      // localhost the landing page is served locally, so it stays relative.
      const baseUrl = isAppDomain() ? 'https://hr4india.com' : '';
      window.location.href = `${baseUrl}/#${sectionId}`;
    }
  };

  const navItems = [
    { label: 'Services', href: 'services', isExternal: false },
    { label: 'OKRHub', href: 'okrhub', isExternal: false },
    { label: 'Features', href: '/features', isExternal: true },
    { label: 'Consult', href: '/consult', isExternal: true },
    { label: 'Resources', href: 'resources', isExternal: false },
    { label: 'Contact', href: 'contact', isExternal: false },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-soft-md'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <a
              href="/"
              className="flex items-center group"
            >
              <img
                src="/hrfi.png"
                alt="HR4India - HR Excellence & OKR Solutions"
                className="h-14 w-auto"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              item.isExternal ? (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-ink-light hover:text-primary-500 font-medium transition-colors"
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="text-ink-light hover:text-primary-500 font-medium transition-colors"
                >
                  {item.label}
                </button>
              )
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-2">
            {/* Theme Picker */}
            <NavThemePicker />

            <Button
              variant="ghost"
              size="sm"
              as="a"
              href="/login"
            >
              Sign In
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => scrollToSection('contact')}
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-ink hover:text-primary-500 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-stone-200 shadow-soft-lg">
          <div className="container mx-auto px-4 py-6 space-y-4">
            {navItems.map((item) => (
              item.isExternal ? (
                <a
                  key={item.href}
                  href={item.href}
                  className="block w-full text-left px-4 py-3 text-ink-light hover:text-primary-500 hover:bg-primary-50 rounded-lg font-medium transition-colors"
                >
                  {item.label}
                </a>
              ) : (
                <button
                  key={item.href}
                  onClick={() => scrollToSection(item.href)}
                  className="block w-full text-left px-4 py-3 text-ink-light hover:text-primary-500 hover:bg-primary-50 rounded-lg font-medium transition-colors"
                >
                  {item.label}
                </button>
              )
            ))}
            <div className="pt-4 border-t border-stone-200 space-y-3">
              <Button
                variant="outline"
                size="md"
                className="w-full"
                as="a"
                href="/login"
              >
                Sign In
              </Button>
              <Button
                variant="primary"
                size="md"
                className="w-full"
                onClick={() => scrollToSection('contact')}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
